/**
 * OrganizationHealthiePatientsTab Component
 * 
 * Displays paginated list of patients (users) from Healthie for an organization
 * Uses Material-UI Table with pagination similar to ProviderClientsTab
 */
import React, { useState, useEffect } from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Typography, Alert, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const OrganizationHealthiePatientsTab = () => {
    const record = useRecordContext();
    
    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [users, setUsers] = useState([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    
    // Check if healthie_data exists and has content
    const healthieData = record?.healthie_data;
    const hasClientGroupId = record?.client_group_id;
    
    // Load users when component mounts or pagination changes
    useEffect(() => {
        if (healthieData?.users) {
            loadUsers();
        } else {
            setLoading(false);
        }
    }, [healthieData?.users, page, rowsPerPage]);
    
    const loadUsers = () => {
        setLoading(true);
        try {
            // For now, we use the users from the initial healthie_data
            // In the future, this could be replaced with a dedicated API endpoint for pagination
            const allUsers = healthieData?.users || [];
            const startIndex = page * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;
            
            setUsers(allUsers.slice(startIndex, endIndex));
            setTotalUsers(healthieData?.users_count || allUsers.length);
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };
    
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    
    // No Healthie UserGroup ID
    if (!hasClientGroupId) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        This organization is not linked to a Healthie Client Group.
                    </Typography>
                </Alert>
            </Box>
        );
    }
    
    // Healthie enrichment failed or no data
    if (!healthieData) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="warning" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        <strong>Unable to load Healthie data</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        The EHR system might be temporarily unavailable, the request may have timed out, or you don't have permission to view this data. 
                        Please refresh the page to try again. Note: Some queries can take 15-30 seconds for organizations with many patients.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                    Patients ({totalUsers})
                </Typography>
                <Chip 
                    label="Healthie EHR" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    icon={<CheckCircleOutlineIcon />}
                />
            </Box>
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={1}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Healthie ID</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user, index) => (
                                <TableRow key={user.id || index}>
                                    <TableCell>{user.full_name || 'N/A'}</TableCell>
                                    <TableCell>{user.email || 'N/A'}</TableCell>
                                    <TableCell>{user.id || 'N/A'}</TableCell>
                                </TableRow>
                            ))}
                            {users.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No patients found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={totalUsers}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default OrganizationHealthiePatientsTab;

